import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'semantic-ui-react';
import UiButton from '../uiButton';
import DropdownBulkSelect from '../dropdownBulkSelect';
import './index.css';

/**
 * Buttons bar crud
 * This component creates a buttons bar to allow CRUD operations:
 * DropdownBulkSelect
 * Button delete
 * Button reload
 * Button cancel
 * Button edit
 * Button save
 * button create
 *
 * Notes:
 * 1. the buttons bar has the following order:
 * [DropdownBulkSelect, button delete, button reload, button cancel, button save and button create]
 * 2. The only button marked as primary is button add and save (optional)
 *
 * Props (parameters)
 * @param showLabels: PropTypes.bool: showLabels === false => the buttons will be shown without label
 * @param sizeButton: PropTypes.string: [mini, tiny, small, medium, large, big, huge, massive]
 * @param dropdownBulkSelect: PropTypes.shape({
 *  show: PropTypes.bool => decide if the DropdownBulkSelect checkbox will be displayed
 *  checkboxDisplay: PropTypes.string => [CHECKBOX_DISPLAY_CHECKED, CHECKBOX_DISPLAY_NOT_CHECKED,
 *                                       CHECKBOX_DISPLAY_INDETERMINATE]
 *  labelAll: PropTypes.string => label name all used in the DropdownBulkSelect
 *  onSelectAll: PropTypes.func => function used in the DropdownBulkSelect
 *  labelNone: PropTypes.string => label name none used in the DropdownBulkSelect
 *  onDeselectAll: PropTypes.func => function used in the DropdownBulkSelect
 * }),
 * @param deleteButton: PropTypes.shape({
 *  show: PropTypes.bool => decide if the button delete is shown
 *  enable: PropTypes.bool => button delete should be enabled
 *  label: PropTypes.string => label button delete
 *  onClick: PropTypes.func =>  function onclick button delete
 * }),
 * @param saveButton: PropTypes.shape({
 *  show: PropTypes.bool => decide if the button save is shown
 *  enable: PropTypes.bool => button save should be enabled
 *  label: PropTypes.string =>  label button save
 *  onClick: PropTypes.func => function onclick button save
 *  primary: PropTypes.bool => button is marked as primary
 * }),
 * @param cancelButton: PropTypes.shape({
 *  show: PropTypes.bool => decide if the button cancel is shown
 *  enable: PropTypes.bool => button cancel should be enabled
 *  label: PropTypes.string => label button cancel
 *  onClick: PropTypes.func => function onclick button cancel
 * }),
 * @param reloadButton: PropTypes.shape({
 *  show: PropTypes.bool => decide if the button reload is shown
 *  enable: PropTypes.bool => button reload should be enabled
 *  label: PropTypes.string => label button reload
 *  onClick: PropTypes.func => function onClick button reload
 * }),
 * @param editButton: PropTypes.shape({
 *  show: PropTypes.bool => decide if the button edit is shown
 *  enable: PropTypes.bool => button edit should be enable
 *  label: PropTypes.string => label button edit
 *  onClick: PropTypes.func => function onClick button edit
 * }),
 * @param addButton: PropTypes.shape({
 *  show: PropTypes.bool => decide if the button add is shown
 *  enable: PropTypes.bool => button add should be enable
 *  label: PropTypes.string => label button add
 *  onClick: PropTypes.func => function onClick button add
 * }),
 * @param string mode
 */
export default class ButtonsBarCRUD extends React.PureComponent {
  render() {
    const {
      showLabels,
      sizeButton,
      dropdownBulkSelect,
      deleteButton,
      saveButton,
      cancelButton,
      reloadButton,
      addButton,
      editButton,
      mode,
    } = this.props;

    return (
      <div className="wrapper-buttons-bar-crud">
        {dropdownBulkSelect.show && (
          <Button
            basic
            className={`${
              mode !== null ? 'button-wrapper-dropdown-bulk-select' : ''
            }`}
          >
            <DropdownBulkSelect
              checkboxDisplay={dropdownBulkSelect.checkboxDisplay}
              labelAll={dropdownBulkSelect.labelAll}
              onClickAll={dropdownBulkSelect.onSelectAll}
              labelNone={dropdownBulkSelect.labelNone}
              onClickNone={dropdownBulkSelect.onDeselectAll}
            />
          </Button>
        )}
        {deleteButton.show && (
          <UiButton
            onClick={deleteButton.onClick}
            disabled={!deleteButton.enable}
            sizeButton={sizeButton}
            iconName="trash"
            showLabels={showLabels}
            label={deleteButton.label}
            mode={mode}
          />
        )}
        {reloadButton.show && (
          <UiButton
            onClick={reloadButton.onClick}
            disabled={!reloadButton.enable}
            sizeButton={sizeButton}
            iconName="redo"
            showLabels={showLabels}
            label={reloadButton.label}
            mode={mode}
          />
        )}
        {cancelButton.show && (
          <UiButton
            onClick={cancelButton.onClick}
            icon
            disabled={!cancelButton.enable}
            sizeButton={sizeButton}
            iconName="cancel"
            showLabels={showLabels}
            label={cancelButton.label}
            mode={mode}
          />
        )}
        {editButton.show && (
          <UiButton
            onClick={editButton.onClick}
            icon
            disabled={!editButton.enable}
            size={sizeButton}
            iconName="write"
            showLabels={showLabels}
            label={editButton.label}
            mode={mode}
          />
        )}
        {saveButton.show && (
          <UiButton
            onClick={saveButton.onClick}
            icon
            disabled={!saveButton.enable}
            id="saveBulk"
            size={sizeButton}
            iconName="save"
            showLabels={showLabels}
            label={saveButton.label}
            mode={mode}
            primary={saveButton.primary}
          />
        )}
        {addButton.show && (
          <UiButton
            onClick={addButton.onClick}
            icon
            disabled={!addButton.enable}
            size={sizeButton}
            iconName="add"
            showLabels={showLabels}
            label={addButton.label}
            mode={mode}
            primary
          />
        )}
      </div>
    );
  }
}

ButtonsBarCRUD.propTypes = {
  showLabels: PropTypes.bool,
  sizeButton: PropTypes.string,
  dropdownBulkSelect: PropTypes.shape({
    show: PropTypes.bool,
    checkboxDisplay: PropTypes.string,
    labelAll: PropTypes.string,
    onSelectAll: PropTypes.func,
    labelNone: PropTypes.string,
    onDeselectAll: PropTypes.func,
  }),
  deleteButton: PropTypes.shape({
    show: PropTypes.bool,
    enable: PropTypes.bool,
    label: PropTypes.string,
    onClick: PropTypes.func,
  }),
  saveButton: PropTypes.shape({
    show: PropTypes.bool,
    enable: PropTypes.bool,
    label: PropTypes.string,
    onClick: PropTypes.func,
    primary: PropTypes.bool,
  }),
  cancelButton: PropTypes.shape({
    show: PropTypes.bool,
    enable: PropTypes.bool,
    label: PropTypes.string,
    onClick: PropTypes.func,
  }),
  reloadButton: PropTypes.shape({
    show: PropTypes.bool,
    enable: PropTypes.bool,
    label: PropTypes.string,
    onClick: PropTypes.func,
  }),
  editButton: PropTypes.shape({
    show: PropTypes.bool,
    enable: PropTypes.bool,
    label: PropTypes.string,
    onClick: PropTypes.func,
  }),
  addButton: PropTypes.shape({
    show: PropTypes.bool,
    enable: PropTypes.bool,
    label: PropTypes.string,
    onClick: PropTypes.func,
  }),
  mode: PropTypes.string,
};

ButtonsBarCRUD.defaultProps = {
  showLabels: true,
  sizeButton: 'medium',
  dropdownBulkSelect: {
    show: false,
  },
  deleteButton: {
    show: false,
  },
  saveButton: {
    show: false,
    primary: false,
  },
  cancelButton: {
    show: false,
  },
  reloadButton: {
    show: false,
  },
  editButton: {
    show: false,
  },
  addButton: {
    show: false,
  },
  mode: null,
};
